import React from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { injectIntl } from 'react-intl'
import { getComponentsForInlineEdge } from '../helpers/utils'
import { getElementName } from '../helpers/element'
import StateButtons from './StateButton/StateButtons'
import { Flex, theme } from 'antd'
import { RelatedDiagramsModalButton } from '../FunctionalDiagrams'
import { needsPinsForRelatedDiagrams } from '../FunctionalDiagrams/RelatedDiagramsModal'
import { useCheckPermission } from '../hooks/Permissions'
import { useFeatureFlags } from '../hooks/FeatureFlags'
import {
  FUNCTIONAL_DIAGRAMS_FLAG,
  WHITE_COLOR,
  BRIGHT_GREY_COLOR,
} from '../constants'
import Loading from './Loading'
import RelatedContentFor from './RelatedContentButton'

import { setRelatedDiagramsModal } from '../actions/related-diagrams-modal-actions'

const HarnessViewGraphTooltip = ({
  graph,
  element,
  intl,
  componentStates,
  selectedElement,
  historiesForComponents,
}) => {
  const dispatch = useDispatch()
  const isEphemeralVehicleTest = useSelector(
    (state) => state.ephemeralVehicleTest.isEphemeralVehicleTest,
    shallowEqual,
  )
  const vin = useSelector((state) => state.componentState.vin)
  const checkPermission = useCheckPermission()
  const { isLoading, usersFeatureFlags } = useFeatureFlags()

  const createTooltipTextContent = (
    intl,
    nameHeader,
    name,
    idHeader,
    id,
    descriptionHeader,
    description,
    assemblyStation,
    partNumber,
  ) => {
    const tooltipNameHeader = nameHeader
    const tooltipName = name
    const tooltipIdHeader = idHeader
    const tooltipId = id

    const tooltipDescriptionHeader = descriptionHeader
      ? descriptionHeader
      : null
    const tooltipDescription = description ? description : null

    const tooltipAssemblyStationHeader =
      (assemblyStation &&
        intl.formatMessage({ id: 'generic.assemblyStation' })) ||
      null
    const tooltipAssemblyStation = assemblyStation || null

    const tooltipPartNumber = partNumber || null
    return {
      tooltipNameHeader,
      tooltipName,
      tooltipIdHeader,
      tooltipId,
      tooltipDescriptionHeader,
      tooltipDescription,
      tooltipAssemblyStationHeader,
      tooltipAssemblyStation,
      tooltipPartNumber,
    }
  }

  const getComponentTooltipContent = (intl, element) => {
    return createTooltipTextContent(
      intl,
      intl.formatMessage({ id: 'generic.component' }),
      getElementName(element.data()),
      intl.formatMessage({ id: 'generic.id' }),
      element.data('id'),
      intl.formatMessage({ id: 'generic.description' }),
      element.data('description'),
      element.data('assembly_station'),
      element.data('part_number'),
    )
  }

  const getHarnessTooltipContent = (intl, element) => {
    return createTooltipTextContent(
      intl,
      intl.formatMessage({ id: 'tracer.toolTip.harnessPartNumber' }),
      element.data('harness') + ' ' + element.data('level'),
      intl.formatMessage({ id: 'generic.description' }),
      element.data('description'),
      element.data('assembly_station'),
    )
  }

  const getDeviceTooltipContent = (intl, element) => {
    return createTooltipTextContent(
      intl,
      intl.formatMessage({ id: 'generic.component' }),
      getElementName(element.data()),
      intl.formatMessage({ id: 'generic.id' }),
      element.data('id'),
      intl.formatMessage({ id: 'generic.description' }),
      element.data('description'),
      element.data('assembly_station'),
      element.data('part_number'),
    )
  }

  const getInlineTooltipContent = (intl, componentStates, element, graph) => {
    const inlineComponents = getComponentsForInlineEdge(
      graph,
      element,
      componentStates,
    )
    const inlineComponentsArr = inlineComponents.map(
      (component) => component.element,
    )

    return createTooltipTextContent(
      intl,
      intl.formatMessage({ id: 'generic.component' }),
      `${getElementName(inlineComponentsArr[0])} - ${getElementName(
        inlineComponentsArr[1],
      )}`,
      intl.formatMessage({ id: 'generic.id' }),
      `${inlineComponentsArr[0].id} - ${inlineComponentsArr[1].id}`,
      intl.formatMessage({ id: 'generic.description' }),
      element.data('description'),
      element.data('assembly_station'),
    )
  }

  const createTooltipContent = () => {
    if (element.isEdge()) {
      if (element.data('edge_type')) {
        return getInlineTooltipContent(intl, componentStates, element, graph)
      } else {
        return getHarnessTooltipContent(intl, element)
      }
    } else if (element.isNode()) {
      if (element.data().category === 'device') {
        return getDeviceTooltipContent(intl, element)
      } else {
        return getComponentTooltipContent(intl, element)
      }
    }
  }

  // Which entity the Related Content button asks about, following exactly the
  // branching createTooltipContent uses above so the two cannot drift: a device
  // node asks the device question, any other node the connector one, and a
  // harness edge the harness one.
  //
  // An inline edge is two connectors rather than one entity, so it gets no
  // button -- the same reason the Related Diagrams button beside it is disabled
  // for edges.
  const relatedContentTarget = () => {
    if (element.isEdge()) {
      return element.data('edge_type')
        ? null
        : {
            kind: 'harness',
            targets: [
              { entityKind: 'harness', harness_id: element.data('harness') },
            ],
          }
    }
    if (element.data().category === 'device') {
      return {
        kind: 'device',
        targets: [{ entityKind: 'device', device_id: element.data('id') }],
      }
    }
    return {
      kind: 'connector',
      targets: [
        { entityKind: 'connector', connector_id: element.data('id') },
        { entityKind: 'connector_part', connector_id: element.data('id') },
      ],
    }
  }

  // Deliberately not part of the target above: everything in that object is
  // spread into the request as an identifier, and a display label is not one.
  const RELATED_CONTENT_LABELS = {
    harness: 'generic.harness',
    device: 'generic.device',
    connector: 'generic.connector',
  }

  const relatedContent = relatedContentTarget()

  // Get Cytoscape scratchpad data.
  // More info: https://js.cytoscape.org/#ele.scratch
  const destroyTooltip = () => element.scratch('_tip').destroy()

  const toolTipContent = createTooltipContent()
  if (isLoading) return <Loading />
  return (
    <div>
      <h6>{toolTipContent.tooltipNameHeader}</h6>
      {element.data().category == 'splice' ? (
        <p>{toolTipContent.tooltipName} (Splice)</p>
      ) : (
        <p>{toolTipContent.tooltipName}</p>
      )}
      <h6>{toolTipContent.tooltipIdHeader}</h6>
      <p>
        <Flex gap="small" align="center" wrap>
          {toolTipContent.tooltipId}{' '}
          {usersFeatureFlags.includes(FUNCTIONAL_DIAGRAMS_FLAG) &&
            checkPermission('page-functional-diagrams:get') && (
              <RelatedDiagramsModalButton
                showModal={() => {
                  destroyTooltip()
                  dispatch(
                    setRelatedDiagramsModal({
                      vin,
                      componentId: element.data('id'),
                    }),
                  )
                }}
                // No pin selection exists here, so the categories that need one
                // can never be answered from a tooltip.
                disabled={
                  element.data('isEdge') === true ||
                  needsPinsForRelatedDiagrams(element.data('category'))
                }
              />
            )}
          {/* The tooltip card is white while the app around it may be dark, so
              this button needs the light treatment on two counts.
              RelatedDiagramsModalButton forces the light algorithm because antd
              derives a disabled button's colours from it, and inheriting the
              dark one leaves a near-transparent box with a near-white icon.
              That alone is not enough here: a nested ConfigProvider inherits
              component tokens, and ComponentThemeGenerator pins
              Button.colorBgContainer to CHARCOAL_NAVY in dark mode. The
              algorithm would then pick a dark icon to sit on that dark fill, so
              the fill has to be reset too -- the same reason
              RelatedDiagramsButton pins the primary tokens for its own type. */}
          {relatedContent && (
            <RelatedContentFor
              targets={relatedContent.targets}
              onBeforeOpen={destroyTooltip}
              // Scoped to the button. Wrapping the component wrapped its modal
              // too, which then rendered dark GUNMETAL backgrounds -- inherited
              // component tokens -- under near-black text from this algorithm.
              buttonTheme={{
                algorithm: theme.defaultAlgorithm,
                components: {
                  Button: {
                    colorBgContainer: WHITE_COLOR,
                    defaultHoverBg: BRIGHT_GREY_COLOR,
                  },
                },
              }}
              details={{
                label: RELATED_CONTENT_LABELS[relatedContent.kind],
                pin: toolTipContent.tooltipName,
                description: toolTipContent.tooltipDescription,
              }}
            />
          )}
        </Flex>
      </p>
      {toolTipContent.tooltipPartNumber && (
        <>
          <h6>P/N</h6>
          <p>{toolTipContent.tooltipPartNumber}</p>
        </>
      )}
      <h6>{toolTipContent.tooltipDescriptionHeader}</h6>
      <p>{toolTipContent.tooltipDescription}</p>
      <h6>{toolTipContent.tooltipAssemblyStationHeader}</h6>
      <p>{toolTipContent.tooltipAssemblyStation}</p>
      {!isEphemeralVehicleTest && (
        <StateButtons onBeforeClick={destroyTooltip} outline tooltip />
      )}
    </div>
  )
}

export default injectIntl(HarnessViewGraphTooltip)
